import { useState } from "react";
import emailjs from "@emailjs/browser";
import { Mail, MapPin, Phone, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const EMAILJS = {
  serviceId: "service_qqknlj6",
  templateId: "template_m5img49",
  publicKey: "m0wc7lSV9dRfSPOWr",
};

const initial = { name: "", email: "", phone: "", message: "" };

export default function Contacto() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa tu nombre, email y mensaje.");
      return;
    }
    setSending(true);
    setStatus(null);
    try {
      await emailjs.send(
        EMAILJS.serviceId,
        EMAILJS.templateId,
        {
          from_name: form.name,
          name: form.name,
          reply_to: form.email,
          email: form.email,
          phone: form.phone || "No proporcionado",
          message: form.message,
          to_name: "Sagitta Corporativo",
        },
        { publicKey: EMAILJS.publicKey }
      );
      setStatus("success");
      setForm(initial);
      toast.success("Mensaje enviado. Te contactaremos a la brevedad.");
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
      toast.error(
        "Ocurrió un error al enviar el mensaje. Inténtalo nuevamente."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — Info */}
          <div className="lg:col-span-5">
            <div className="reveal eyebrow mb-5">Contacto</div>
            <h2 className="reveal font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-neutral-950">
              Contáctanos,{" "}
              <span className="italic text-[#E97906]">vamos a trabajar</span>{" "}
              juntos.
            </h2>
            <p className="reveal mt-6 text-neutral-600 text-base lg:text-[17px] leading-relaxed max-w-md">
              Cuéntanos tu situación. Un socio experto te responderá
              personalmente para evaluar el alcance y el camino estratégico
              ideal.
            </p>

            <div className="reveal mt-10 space-y-6">
              <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center w-11 h-11 border border-neutral-200 text-[#E97906] shrink-0">
                  <MapPin size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                    Dirección
                  </div>
                  <div className="text-neutral-900 mt-1 leading-relaxed max-w-sm">
                    Calle Letran No. 4341, Int. 3, C.P. 45039, Residencial
                    Cordilleras, Zapopan, Jalisco, México.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center w-11 h-11 border border-neutral-200 text-[#E97906] shrink-0">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                    Teléfono
                  </div>
                  <div
                    data-testid="contact-phone"
                    className="text-neutral-900 mt-1"
                  >
                    Próximamente
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center w-11 h-11 border border-neutral-200 text-[#E97906] shrink-0">
                  <Mail size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                    Email
                  </div>
                  <a
                    href="mailto:contacto@corporativo-sagitta.com"
                    data-testid="contact-email"
                    className="text-neutral-900 mt-1 inline-block link-underline"
                  >
                    contacto@corporativo-sagitta.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="reveal bg-white border border-neutral-200 p-8 lg:p-12"
            >
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <label
                    htmlFor="name"
                    className="text-[11px] uppercase tracking-[0.24em] text-neutral-500"
                  >
                    Nombre completo *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Tu nombre"
                    required
                    data-testid="contact-input-name"
                    className="sg-input"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="text-[11px] uppercase tracking-[0.24em] text-neutral-500"
                  >
                    Email *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="correo@ejemplo.com"
                    required
                    data-testid="contact-input-email"
                    className="sg-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="phone"
                    className="text-[11px] uppercase tracking-[0.24em] text-neutral-500"
                  >
                    Teléfono
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+52 33 0000 0000"
                    data-testid="contact-input-phone"
                    className="sg-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label
                    htmlFor="message"
                    className="text-[11px] uppercase tracking-[0.24em] text-neutral-500"
                  >
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Cuéntanos brevemente cómo podemos ayudarte"
                    required
                    data-testid="contact-input-message"
                    className="sg-input"
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                <p className="text-xs text-neutral-500 max-w-md">
                  Al enviar este formulario confirmas tu interés en recibir una
                  comunicación profesional de Sagitta Corporativo.
                </p>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="btn-shine inline-flex items-center gap-3 bg-[#E97906] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-[#0A0A0A] transition-colors duration-300"
                >
                  {sending ? "Enviando..." : "Enviar mensaje"}
                  <Send size={16} />
                </button>
              </div>

              {status === "success" && (
                <div
                  data-testid="contact-success"
                  className="mt-6 flex items-start gap-3 p-4 bg-green-50 border border-green-200 text-green-900 text-sm"
                >
                  <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                  Gracias. Tu mensaje fue enviado correctamente.
                </div>
              )}
              {status === "error" && (
                <div
                  data-testid="contact-error"
                  className="mt-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-900 text-sm"
                >
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  Ocurrió un error. Por favor intenta nuevamente o escríbenos
                  directamente al correo.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
